import { oc } from "@orpc/contract";
import { z } from "zod";

export const FirstMessageMode = {
  AssistantSpeaksFirst: "assistant-speaks-first",
  AssistantWaitsForUser: "assistant-waits-for-user",
  ModelGenerated: "assistant-speaks-first-with-model-generated-message",
} as const;

export const BackgroundSound = {
  Off: "off",
  Office: "office",
} as const;

export const TranscriberProvider = {
  Deepgram: "deepgram",
  Gladia: "gladia",
  Google: "google",
} as const;

export const VoiceProvider = {
  OpenAI: "openai",
  Azure: "azure",
  ElevenLabs: "11labs",
  Cartesia: "cartesia",
} as const;

export const LanguagePreset = {
  Auto: "auto",
  English: "english",
  Spanish: "spanish",
} as const;

export type LanguagePresetValue =
  (typeof LanguagePreset)[keyof typeof LanguagePreset];

export const LanguagePresetSchema = z.enum([
  LanguagePreset.Auto,
  LanguagePreset.English,
  LanguagePreset.Spanish,
]);

export const LANGUAGE_PRESET_CONFIGS: Record<
  LanguagePresetValue,
  {
    transcriber: { provider: string; model: string; language: string };
    voice?: {
      provider: string;
      voiceId: string;
      model?: string;
      instructions?: string;
    };
  }
> = {
  [LanguagePreset.Auto]: {
    transcriber: {
      provider: TranscriberProvider.Deepgram,
      model: "nova-2",
      language: "multi",
    },
  },
  [LanguagePreset.English]: {
    transcriber: {
      provider: TranscriberProvider.Deepgram,
      model: "nova-2",
      language: "en",
    },
  },
  [LanguagePreset.Spanish]: {
    transcriber: {
      provider: TranscriberProvider.Deepgram,
      model: "nova-2",
      language: "es",
    },
    voice: {
      provider: VoiceProvider.OpenAI,
      voiceId: "nova",
      model: "gpt-4o-mini-tts",
      instructions:
        "Speak with a native Latin American Spanish accent. Be warm and professional.",
    },
  },
};

export const TranscriberSettingsSchema = z.object({
  provider: z.enum([
    TranscriberProvider.Deepgram,
    TranscriberProvider.Gladia,
    TranscriberProvider.Google,
  ]),
  language: z.string().optional(),
  model: z.string().optional(),
});

export type TranscriberSettings = z.infer<typeof TranscriberSettingsSchema>;

export const VoiceOverrideSettingsSchema = z.object({
  provider: z.enum([
    VoiceProvider.OpenAI,
    VoiceProvider.Azure,
    VoiceProvider.ElevenLabs,
    VoiceProvider.Cartesia,
  ]),
  voiceId: z.string().min(1),
  language: z.string().optional(),
  model: z.string().optional(),
  instructions: z.string().optional(),
});

export type VoiceOverrideSettings = z.infer<typeof VoiceOverrideSettingsSchema>;

export const VoiceAgentAdvancedSettingsSchema = z.object({
  firstMessageMode: z
    .enum([
      FirstMessageMode.AssistantSpeaksFirst,
      FirstMessageMode.AssistantWaitsForUser,
      FirstMessageMode.ModelGenerated,
    ])
    .optional(),
  maxDurationSeconds: z.number().int().positive().optional(),
  silenceTimeoutSeconds: z.number().int().positive().optional(),
  endCallMessage: z.string().optional(),
  endCallPhrases: z.array(z.string()).optional(),
  voicemailMessage: z.string().optional(),
  backgroundSound: z
    .enum([BackgroundSound.Off, BackgroundSound.Office])
    .optional(),
  backgroundDenoisingEnabled: z.boolean().optional(),
  hipaaEnabled: z.boolean().optional(),
  recordingEnabled: z.boolean().optional(),
  languagePreset: LanguagePresetSchema.optional(),
  transcriber: TranscriberSettingsSchema.optional(),
  voiceOverride: VoiceOverrideSettingsSchema.optional(),
});

export type VoiceAgentAdvancedSettings = z.infer<
  typeof VoiceAgentAdvancedSettingsSchema
>;

export const ResourceOrigin = {
  Created: "created",
  Linked: "linked",
} as const;

export const ResourceOriginSchema = z.enum([
  ResourceOrigin.Created,
  ResourceOrigin.Linked,
]);

export const VoiceAgentConfigSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string().min(1),
  assistantId: z.string().min(1),
  voicePhoneNumberId: z.uuid(),
  assistantOrigin: ResourceOriginSchema.nullable(),
  assistantName: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  phoneNumberOrigin: ResourceOriginSchema.nullable(),
  settings: VoiceAgentAdvancedSettingsSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type VoiceAgentConfigDTO = z.infer<typeof VoiceAgentConfigSchema>;

export const CreateVoiceAgentConfigInputSchema = z.object({
  name: z.string().min(1),
  assistantId: z.string().min(1),
  voicePhoneNumberId: z.uuid(),
  assistantOrigin: ResourceOriginSchema.optional(),
  settings: VoiceAgentAdvancedSettingsSchema.optional(),
});

export type CreateVoiceAgentConfigInput = z.infer<
  typeof CreateVoiceAgentConfigInputSchema
>;

export const UpdateVoiceAgentConfigInputSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).optional(),
  assistantId: z.string().min(1).optional(),
  voicePhoneNumberId: z.uuid().optional(),
  assistantOrigin: ResourceOriginSchema.optional(),
  settings: VoiceAgentAdvancedSettingsSchema.optional(),
});

export type UpdateVoiceAgentConfigInput = z.infer<
  typeof UpdateVoiceAgentConfigInputSchema
>;

const listVoiceAgentConfigs = oc
  .output(z.array(VoiceAgentConfigSchema))
  .route({ method: "GET" });

const getVoiceAgentConfig = oc
  .input(z.object({ id: z.uuid() }))
  .output(VoiceAgentConfigSchema)
  .route({ method: "GET" });

const createVoiceAgentConfig = oc
  .input(CreateVoiceAgentConfigInputSchema)
  .output(VoiceAgentConfigSchema);

const updateVoiceAgentConfig = oc
  .input(UpdateVoiceAgentConfigInputSchema)
  .output(VoiceAgentConfigSchema);

const deleteVoiceAgentConfig = oc
  .input(z.object({ id: z.uuid() }))
  .output(z.object({ success: z.boolean() }));

export const voiceAgentConfigContracts = {
  list: listVoiceAgentConfigs,
  get: getVoiceAgentConfig,
  create: createVoiceAgentConfig,
  update: updateVoiceAgentConfig,
  delete: deleteVoiceAgentConfig,
} as const;

export type VoiceAgentConfigContracts = typeof voiceAgentConfigContracts;
